/**
 * Cards API — module entry point for inspiration and intent cards.
 *
 * Endpoint migrated 2026-05-23 from dead catalog-api.mamamiya.store
 * (ADR-002 step 9 retired) to creator-api/api/customer/cards/library.
 * Response envelope: { success, data: [...] }.
 *
 * Usage:
 *   import { fetchIntentCards, fetchStyleCards } from '../modules/create/cards/api.js'
 */

import { apiRequest } from '../../../api/index.js'
import { CONFIG } from '../../../core/config.js'

const CARDS_LIBRARY = `${CONFIG.FRONTEND_API}/api/customer/cards/library`

/**
 * @typedef {Object} Card
 * @property {string} id
 * @property {'intent'|'style'|'tool'} card_type
 * @property {string} category
 * @property {string} title
 * @property {string} [description]
 * @property {'prompt'|'hybrid'|'api_direct'|'ideogram'} execution_mode
 * @property {string} [requires_model]
 * @property {boolean} requires_second_image
 * @property {string} [prompt_template]
 * @property {string} [preserve_template]
 * @property {string} [compatible_categories]
 * @property {object|null} quick_options
 * @property {string} [card_color]
 */

/**
 * Fetch intent cards (card_type='intent') from the cards library.
 * Returns 11 cards seeded in migration 146.
 */
export async function fetchIntentCards() {
  const data = await apiRequest(`${CARDS_LIBRARY}?card_type=intent&limit=50`)
  return (data.data || []).map(normalizeIntentCard)
}

// New cards schema (create-core) → the vocabulary the renderer + matrix dispatch
// expect. `execution_mode` is already the matrix dispatch key; map title ← name and
// default_on_categories ← compatible_categories, coerce requires_second_image to a
// boolean. why → ADR-005 2.0.0 (matrix alignment) / review P0-1.
function normalizeIntentCard(c) {
  return {
    ...c,
    title: c.title ?? c.name,
    default_on_categories: c.default_on_categories ?? c.compatible_categories ?? null,
    requires_second_image: !!c.requires_second_image,
  }
}

/**
 * Fetch style / inspiration cards (card_type='style').
 * @param {string} [category] - optional category filter (e.g. 'Scene', 'Lighting')
 */
export async function fetchStyleCards(category) {
  const params = new URLSearchParams({ card_type: 'style', limit: '500' })
  if (category) params.set('category', category)
  const data = await apiRequest(`${CARDS_LIBRARY}?${params}`)
  return data.data || []
}

/**
 * Fetch negative-prompt cards (card_type='negative').
 * Returns empty array if endpoint returns no results.
 */
export async function fetchNegativeCards() {
  const data = await apiRequest(`${CARDS_LIBRARY}?card_type=negative&limit=50`)
  // create-core schema uses `name`; negative-prompt-card reads `title` (and splits
  // `description` into tags). Map title←name. why → name/title schema drift.
  return (data.data || []).map((c) => ({ ...c, title: c.title ?? c.name }))
}
